<template>
  <div class="cdc-panel px">
    <div class="cdc-title">
      <span>{{ $t("cyberboardR6.connectionPanelTitle") }}</span>
      <a-tooltip
        placement="rightTop"
        overlayClassName="r6-connection-tooltip px"
        :title="$t('cyberboardR6.connectionHint')"
        :trigger="['hover', 'focus', 'click']"
      >
        <button
          class="cdc-help-icon"
          type="button"
          :aria-label="$t('cyberboardR6.connectionHint')"
        >
          <a-icon type="question-circle" aria-hidden="true" />
        </button>
      </a-tooltip>
      <span
        class="cdc-sync-status"
        :class="{ active: visibleSyncStatus }"
        role="status"
        aria-live="polite"
        :title="
          visibleSyncStatus ? $t(`cyberboardR6.${visibleSyncStatus}`) : ''
        "
      >
        <span>(</span>
        <span class="cdc-sync-text">
          {{ visibleSyncStatus ? $t(`cyberboardR6.${visibleSyncStatus}`) : "" }}
        </span>
        <span>)</span>
      </span>
    </div>
    <div class="cdc-status">{{ statusText }}</div>
    <div
      v-if="state !== 'connected'"
      class="cdc-status"
      role="status"
      aria-live="polite"
    >
      {{ $t("cyberboardR6.offlineHint") }}
    </div>
    <div class="cdc-actions">
      <button
        class="cdc-button"
        type="button"
        :disabled="state === 'connecting'"
        @click="connect"
      >
        {{ state === "connected" ? $t("refresh") : connectText }}
      </button>
      <button
        v-if="state === 'connected'"
        class="cdc-button secondary"
        type="button"
        @click="disconnect"
      >
        {{ disconnectText }}
      </button>
    </div>
  </div>
</template>

<script>
import { deepCopy } from "@/utils";
import { CyberboardR6Bridge } from "./utils/cyberboardR6Bridge";

const wait = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

let sharedBridge = null;

function traceEnabled() {
  if (process.env.VUE_APP_R6_CDC_TRACE === "true") return true;
  try {
    // 浏览器本地开关可即时启用日志，无需重新构建。
    return (
      typeof window !== "undefined" &&
      window.localStorage.getItem("r6CdcTrace") === "1"
    );
  } catch (_) {
    return false;
  }
}

function verboseFrameTraceEnabled() {
  try {
    // Set localStorage.r6CdcRawFrames=1 to include full 64-byte protocol frames.
    return (
      typeof window !== "undefined" &&
      window.localStorage.getItem("r6CdcRawFrames") === "1"
    );
  } catch (_) {
    return false;
  }
}

export function getCyberboardR6Bridge() {
  if (!sharedBridge) {
    sharedBridge = new CyberboardR6Bridge({
      isLogEnabled: traceEnabled,
      isVerboseFrameLogEnabled: verboseFrameTraceEnabled,
      onLog: (event) => console.info("[R6 CDC]", event),
    });
  }
  return sharedBridge;
}

export default {
  name: "CCyberboardR6CdcPanel",
  props: {
    baseData: {
      type: Object,
      default: () => ({}),
    },
    defaultData: {
      type: Object,
      default: () => ({}),
    },
    syncStatus: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      bridge: null,
      state: "disconnected",
      deviceInfo: null,
      errorText: "",
      recoveringAfterReset: false,
      recoveryError: null,
      visibleSyncStatus: this.syncStatus,
      syncStatusTimer: null,
    };
  },
  computed: {
    statusText() {
      if (this.errorText) return this.errorText;
      if (this.state === "connecting") {
        return this.$t("cyberboardR6.connecting");
      }
      if (this.state === "connected") {
        const model = this.deviceInfo?.productModel;
        return [
          this.$t("cyberboardR6.connected"),
          model ? `CYBERBOARD ${model}` : "",
        ]
          .filter(Boolean)
          .join(" ");
      }
      return this.$t("cyberboardR6.disconnected");
    },
    connectText() {
      return this.$t("cyberboardR6.connect");
    },
    disconnectText() {
      return this.$t("cyberboardR6.disconnect");
    },
  },
  async mounted() {
    this.bridge = getCyberboardR6Bridge();
    this.bridge.on("connected", this.onConnected);
    this.bridge.on("disconnected", this.onDisconnected);
    this.bridge.on("error", this.onError);
    if (this.bridge.isConnected && this.bridge.currentData) {
      this.deviceInfo = this.bridge.deviceInfo;
      // 路由复用共享 bridge；不能用设备快照覆盖 Vuex 中尚未手动下发的编辑。
      this.state = "connected";
      this.$emit("ready", this.bridge);
      this.$emit("info", this.deviceInfo);
      this.$emit("state", this.state);
      return;
    }
    try {
      const port = await this.bridge.connectExisting();
      if (port) await this.finishConnection();
    } catch (error) {
      await this.bridge.disconnect();
      this.onError(error);
    }
  },
  beforeDestroy() {
    clearTimeout(this.syncStatusTimer);
    if (!this.bridge) return;
    this.bridge.off("connected", this.onConnected);
    this.bridge.off("disconnected", this.onDisconnected);
    this.bridge.off("error", this.onError);
  },
  watch: {
    syncStatus: {
      immediate: true,
      handler(status) {
        clearTimeout(this.syncStatusTimer);
        this.visibleSyncStatus = status;
        if (status === "keySyncSynced" || status === "syncSynced") {
          this.syncStatusTimer = setTimeout(() => {
            this.visibleSyncStatus = "";
          }, 1800);
        }
      },
    },
  },
  methods: {
    async connect() {
      const wasConnected = this.bridge.isConnected;
      this.errorText = "";
      this.state = "connecting";
      this.$emit("state", this.state);
      try {
        // 刷新时先释放旧端口，允许在串口选择框切换到另一台设备。
        if (wasConnected) await this.bridge.disconnect();
        const existingPort = wasConnected
          ? null
          : await this.bridge.connectExisting();
        if (!existingPort) await this.bridge.connect();
        await this.finishConnection(false);
      } catch (error) {
        await this.bridge.disconnect();
        this.onError(error);
      }
    },
    async finishConnection(preserveConnectionOnFailure = false) {
      const baseData = this.bridge.isFreshConnection
        ? deepCopy(this.defaultData)
        : this.baseData;
      const result = await this.bridge.syncJSON(baseData);
      if (!result) {
        // 刷新读回失败不应关闭一个仍在线的 CDC 端口。
        if (!preserveConnectionOnFailure) await this.bridge.disconnect();
        this.state =
          preserveConnectionOnFailure && this.bridge.isConnected
            ? "connected"
            : "disconnected";
        this.$emit("state", this.state);
        return false;
      }
      this.deviceInfo = result.info;
      this.$emit("data", result.data, result.data, result.info);
      this.state = "connected";
      this.$emit("ready", this.bridge);
      this.$emit("info", this.deviceInfo);
      this.$emit("state", this.state);
      return true;
    },
    async reconnectAfterReset() {
      this.recoveringAfterReset = true;
      this.recoveryError = null;
      this.errorText = "";
      if (this.bridge.isConnected) await this.bridge.disconnect();
      this.state = "connecting";
      this.$emit("state", this.state);

      const deadline = Date.now() + 15000;
      let lastError;
      while (Date.now() < deadline) {
        try {
          const port = await this.bridge.connectExisting();
          if (port && (await this.finishConnection())) {
            this.recoveringAfterReset = false;
            return true;
          }
          lastError = this.recoveryError || lastError;
          if (
            lastError?.message.includes("设备型号不匹配") &&
            !lastError.message.includes("未知设备")
          ) {
            break;
          }
        } catch (error) {
          lastError = error;
        }
        if (this.bridge.isConnected) await this.bridge.disconnect();
        await wait(300);
        if (Date.now() < deadline) {
          this.errorText = "";
          this.state = "connecting";
          this.$emit("state", this.state);
        }
      }

      this.recoveringAfterReset = false;
      this.state = "disconnected";
      this.errorText =
        lastError?.message || "R6 CDC 设备复位后重新连接超时，请点击刷新重试";
      this.$emit("state", this.state);
      this.$emit("error", lastError || new Error(this.errorText));
      return false;
    },
    async disconnect() {
      await this.bridge.disconnect();
    },
    onConnected() {
      this.errorText = "";
    },
    onDisconnected() {
      this.state = "disconnected";
      this.deviceInfo = null;
      this.$emit("state", this.state);
      this.$emit("info", null);
    },
    onError(error) {
      if (this.recoveringAfterReset) {
        this.recoveryError = error;
        return;
      }
      this.errorText = this.$t("cyberboardR6.operationFailed");
      this.$emit("error", error);
    },
  },
};
</script>

<style lang="scss" scoped>
.px.cdc-panel {
  position: fixed;
  top: 80px;
  left: 20px;
  z-index: 5;
  width: 230px;
  max-width: calc(100vw - 40px);
  padding: 12px 14px;
  border: 1px solid #454545;
  border-radius: 8px;
  background: rgba(23, 23, 23, 0.96);
  text-align: left;
  font-size: 14px;
  line-height: 1.5;
  // 面板覆盖在键盘上方时不应拦截底层键位点击。
  pointer-events: none;
}

.px .cdc-title {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 26px;
  color: $primary-color;
  font-size: 17px;
  line-height: 26px;
  white-space: nowrap;
}

.px .cdc-help-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #999;
  cursor: help;
  pointer-events: auto;
}

.px .cdc-status {
  margin-top: 6px;
  word-break: break-word;
}

.px .cdc-sync-status {
  display: none;

  &.active {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    min-width: 0;
    font-size: 14px;
    font-weight: normal;
    vertical-align: middle;
  }
}

.px .cdc-sync-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.px .cdc-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.px .cdc-button {
  min-height: 32px;
  padding: 5px 12px;
  font-size: 14px;
  line-height: 20px;
  border: 1px solid $primary-color;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  pointer-events: auto;

  &:disabled {
    cursor: wait;
    opacity: 0.6;
  }

  &.secondary {
    border-color: #666;
  }
}
</style>

<style lang="scss">
.r6-connection-tooltip.px {
  max-width: 320px;

  .ant-tooltip-inner {
    padding: 10px 12px;
    background: #303030;
    color: #f5f5f5;
    font-size: 14px;
    line-height: 1.6;
  }

  .ant-tooltip-arrow::before {
    background: #303030;
  }
}
</style>
